/**
 * 审核弹窗组件
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Row, Col, Upload, Button, Image, Radio, Select, DatePicker, UploadFile } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { formSearchGutter } from '@/constants/common';
import dayjs from 'dayjs';
import localStorageUtil from '@/utils/localStorageUtil';
import { OssEntity } from '@/service/driverArchive/define';

const { TextArea } = Input;
const { Option } = Select;

interface ReviewModalProps {
  visible: boolean;
  mode: 'view' | 'edit'; // view: 查看模式, edit: 编辑模式
  onCancel: () => void;
  onOk: (values: any) => void;
  record: any;
  fleetOptions?: Array<{ value: string | number; label: string }>; // 车队选项
  fetchFleetList?: (name?: string) => void; // 车队搜索函数
}
interface iImgFileDisplay {
  driverImageFileUrl: UploadFile[];
  idCardFileUrl: UploadFile[];
  driveLicenseFileUrl: UploadFile[];
}
/**
 * 审核弹窗组件
 */
const ReviewModal: React.FC<ReviewModalProps> = ({ visible, mode, onCancel, onOk, record, fleetOptions = [], fetchFleetList }) => {
  const [form] = Form.useForm();
  const { userDetail } = useSelector((state: any) => state.userStore);
  // console.log('userDetail in ReviewModal:', userDetail);
  const isView = mode === 'view';
  const modalTitle = isView ? '查看审核结果' : '司机登记信息审核';

  const [fileLists, setFileLists] = useState<iImgFileDisplay>({
    driverImageFileUrl: [],
    idCardFileUrl: [],
    driveLicenseFileUrl: [],
  });

  useEffect(() => {
    // 初始化图片文件列表 - 从 OssEntity[] 转换
    const newFileLists: iImgFileDisplay = {
      driverImageFileUrl: [],
      idCardFileUrl: [],
      driveLicenseFileUrl: [],
    };
    if (visible && record) {
      // 将后端数据字段映射到表单字段
      const formValues = {
        ...record,
        // 确保 companyId 是正确的类型（可能需要转换为数字或字符串）
        companyId: record.companyId || (fleetOptions.find(f => f.label === record.companyName)?.value),
        // 将字符串日期转换为 dayjs 对象
        birthday: record.birthday ? dayjs(record.birthday) : null,
        // 将字符串日期时间转换为 dayjs 对象
        createTime: record.createTime ? dayjs(record.createTime) : null,
        // 根据 driverId 设置同步状态
        isSynced: record.driverId ? '是' : '否',
      };

      form.setFieldsValue(formValues);
      // 初始化图片文件列表
      if (record.driverImageFileUrl) {
        newFileLists.driverImageFileUrl = getDisplayArrFromOssEntities(record.driverImageFileUrl);
      }
      if (record.idCardFileUrl) {
        newFileLists.idCardFileUrl = getDisplayArrFromOssEntities(record.idCardFileUrl);
      }
      if (record.driveLicenseFileUrl) {
        newFileLists.driveLicenseFileUrl = getDisplayArrFromOssEntities(record.driveLicenseFileUrl);
      }

    } else {
      form.resetFields();
    }

    setFileLists(newFileLists);
  }, [visible, record, form, fleetOptions]);

  const getDisplayArrFromOssEntities = (ossEntities: OssEntity[] | null): UploadFile[] => {
    if (!ossEntities || ossEntities.length === 0) return [];
    return ossEntities.map((entity: OssEntity) => ({
      uid: entity.ossKey as string,
      name: entity.ossKey as string,
      url: entity.ossUrl as string,
      thumbUrl: entity.ossUrl as string,
    }));
  };

  const _convertFileListToKeys = (fileList: any[]) => {
        
        if (!fileList || fileList.length === 0) return '';
        // console.log('Converting fileList to keys:', fileList);
        // debugger;
        return fileList.map((file: any) => {
            // 如果是新上传的文件，从 response 中获取 ossKey
            if (file.response?.data) {
              return file.response.data.key;
            }
            // 如果是已存在的文件，从原始数据中获取 ossKey
            if (file.ossKey) {
              return file.ossKey;
            }
            //如果是从接口上传的图片那么在处理数据的时候会把key放在name字段里，所以这里直接取name字段即可
            if(file.name){
              return file.name;
            }
            //如果都没有，抛出错误
            throw new Error('无法获取上传文件的 ossKey');
          })
          .filter((key: string) => key !== '')
          .join(',');
      };

  const handleSubmit = async () => {
    if (isView) {
      onCancel();
      return;
    }

    try {
      const values = await form.validateFields();
      
      // 检查审核状态是否已选择
      if (!values.reviewStat) {
        Modal.warning({
          title: '提示',
          content: '请选择审核状态',
        });
        return;
      }
      
      // console.log(fileLists);
      // 将文件列表转换为逗号分隔的 ossKey 字符串
      

      const finalValues = {
        ...record, // 保留原始记录的所有字段
        ...values, // 表单修改的值覆盖
        // 转换日期字段为字符串格式
        birthday: values.birthday ? dayjs(values.birthday).format('YYYY-MM-DD') : record.birthday,
        // 转换图片字段为逗号分隔的 key 字符串
        driverImageFile: _convertFileListToKeys(fileLists.driverImageFileUrl),
        drivingLicenseFile: _convertFileListToKeys(fileLists.driveLicenseFileUrl),
        idCardFile: _convertFileListToKeys(fileLists.idCardFileUrl),
        // 添加审核人ID
        reviewBy: userDetail?.mobile,
      };
      delete finalValues.createTime; // 删除不可修改字段
      // console.log('Final submitted values:', finalValues);
      // debugger;

      onOk(finalValues);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const handleUploadChange = (info: any, name: string) => {
    setFileLists({ ...fileLists, [name]: info.fileList });
  };

  useEffect(() => {
    console.log('fileLists changed:', fileLists);
  }, [fileLists]);

  // 渲染图片上传/查看组件
  const renderImageField = (name: keyof iImgFileDisplay, label: string, maxCount?: number) => {
    //仅查看模式
    if (isView) {
      const count = fileLists[name]?.length || 0;
      // 只有设置了 maxCount 才显示数量限制
      const countText = maxCount ? ` (${count}/${maxCount})` : '';

      return (
        <Form.Item label={`${label}${countText}`}>
          {fileLists[name]?.length > 0 ? (
            <Image.PreviewGroup>
              {fileLists[name].map((file: any) => (
                <Image key={file.uid} width={100} src={file.url} style={{ marginRight: 8 }} />
              ))}
            </Image.PreviewGroup>
          ) : (
            '暂无图片'
          )}
        </Form.Item>
        // <div>图片预览</div>
      );
    }

    // 编辑模式
    const targetFileList = fileLists[name] || [];
    
    return (
      <Form.Item label={label}  >
        <Upload
          action="/api/v2/resource/upload"
          listType="picture"
          fileList={targetFileList || []}
          onChange={(info) => handleUploadChange(info, name)}
          headers={{ access_token: localStorageUtil.getItem('access_token') }}
          maxCount={maxCount}
        >
          <Button icon={<UploadOutlined />}>上传图片</Button>
        </Upload>
      </Form.Item>
    );
  };

  // 判断是否为审核模式（编辑模式）
  const isEditMode = mode === 'edit';

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={1200}
      okText={isView ? '关闭' : '确定'}
      cancelButtonProps={{ style: isView ? { display: 'none' } : {} }}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="name"
              label="司机姓名"
              rules={[{ required: isEditMode, message: '请输入司机姓名' }]}
            >
              <Input placeholder="请输入司机姓名" disabled={isView} />
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="gender"
              label="司机性别"
              rules={[{ required: isEditMode, message: '请选择司机性别' }]}
            >
              <Radio.Group disabled={isView}>
                <Radio value="男">男</Radio>
                <Radio value="女">女</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="placeOfOrigin"
              label="籍贯"
              rules={[{ required: isEditMode, message: '请输入籍贯' }]}
            >
              <Input placeholder="请输入籍贯" disabled={isView} />
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="birthday"
              label="出生日期"
              rules={[{ required: isEditMode, message: '请选择出生日期' }]}
            >
              <DatePicker
                placeholder="请选择出生日期"
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                disabled={isView}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="phone"
              label="联系电话"
              rules={[{ required: isEditMode, message: '请输入联系电话' }]}
            >
              <Input placeholder="请输入联系电话" disabled={isView} />
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            {isView ? (
              <Form.Item label="所属车队">
                <Input
                  value={record?.companyName || fleetOptions.find(f => f.value === record?.companyId)?.label || ''}
                  disabled
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="companyId"
                label="所属车队"
                rules={[{ required: isEditMode, message: '请选择所属车队' }]}
              >
                <Select
                  placeholder="请选择所属车队"
                  allowClear
                  showSearch
                  onSearch={(value) => fetchFleetList?.(value)}
                  filterOption={false}
                >
                  {fleetOptions.map((fleet) => (
                    <Option key={fleet.value} value={fleet.value}>
                      {fleet.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            {renderImageField('driverImageFileUrl', '司机照片', undefined)}
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="maritalStat"
              label="婚姻情况"
            >
              <Select placeholder="请选择婚姻情况" allowClear disabled={isView}>
                <Option value="已婚">已婚</Option>
                <Option value="未婚">未婚</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="fertilityStat"
              label="生育情况"
            >
              <Select placeholder="请选择生育情况" allowClear disabled={isView}>
                <Option value="已育">已育</Option>
                <Option value="未育">未育</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="drivingAge"
              label="驾龄"
            >
              <Input placeholder="请输入驾龄" addonAfter="年" disabled={isView} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="permResidence"
              label="常居住地"
            >
              <Input placeholder="请输入常居住地" disabled={isView} />
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="familyEconStat"
              label="家庭经济情况"
            >
              <Input placeholder="请输入家庭经济情况" disabled={isView} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="personality"
              label="性格"
            >
              <Input placeholder="请输入性格特点" disabled={isView} />
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="assistDrivingExp"
              label="是否有辅助驾驶设备使用经验"
            >
              <Select placeholder="请选择" allowClear disabled={isView}>
                <Option value="有">有</Option>
                <Option value="无">无</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="mandarinAbil"
              label="普通话能力"
            >
              <Select placeholder="请选择普通话能力" allowClear disabled={isView}>
                <Option value="标准">标准</Option>
                <Option value="一般">一般</Option>
                <Option value="不会">不会</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="cargoTransQual"
              label="货物运输资质"
            >
              <Input placeholder="请输入货物运输资质" disabled={isView} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="safetyAware"
              label="安全意识"
            >
              <Input placeholder="请输入安全意识评估" disabled={isView} />
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            {renderImageField('driveLicenseFileUrl', '驾驶证照片', 2)}
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            {renderImageField('idCardFileUrl', '身份证照片', 2)}
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="createTime"
              label="信息采集时间"
            >
              <DatePicker
                showTime
                placeholder="请选择信息采集时间"
                style={{ width: '100%' }}
                format="YYYY-MM-DD HH:mm:ss"
                disabled
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            <Form.Item
              name="createByName"
              label="信息采集人员"
            >
              <Input placeholder="信息采集人员" disabled />
            </Form.Item>
          </Col>

          {/* 右列 */}
          <Col span={12}>
            <Form.Item
              name="isSynced"
              label="是否已同步司机档案"
            >
              <Radio.Group disabled>
                <Radio value="是">是</Radio>
                <Radio value="否">否</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 左列 */}
          <Col span={12}>
            {
              isEditMode && (
                <Form.Item
                  name="reviewStat"
                  label="审核状态"
                  rules={[{ required: true, message: '请选择审核状态' }]}
                >
                  <Radio.Group>
                    {/* 
                    审核状态是两个选项：1 - 审核不通过，2 - 审核通过  这是所有审核状态的一个子集
                    是硬编码
                    */}
                    <Radio value={2}>审核通过</Radio>
                    <Radio value={1}>审核不通过</Radio>
                  </Radio.Group>
                </Form.Item>
              )
            }
            {isView && (
              <Form.Item
                name="reviewByName"
                label="审核人员"
              >
                <Input placeholder="审核人员" disabled />
              </Form.Item>
            )}
          </Col>

          {/* 右列 - 留空保持对齐 */}
          <Col span={12}></Col>
        </Row>

        <Row gutter={formSearchGutter}>
          {/* 审核备注占满整行 */}
          <Col span={24}>
            <Form.Item
              name="reviewRemark"
              label="审核备注"
            >
              <TextArea
                rows={4}
                placeholder="请输入审核备注"
                maxLength={500}
                showCount
                disabled={isView}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ReviewModal;
